import { Link } from 'react-router-dom';
import { ArrowRight, CheckCircle, FileText, Percent, Globe } from 'lucide-react';
import SEO from '../../components/SEO';
import SeoNav from '../../components/SeoNav';

const schema = {
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  mainEntity: [
    {
      '@type': 'Question',
      name: 'What should a Pakistani invoice include?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'A Pakistani invoice should include: your business name and address; your NTN (National Tax Number) if registered; the client name and address; a unique invoice number; the invoice date; a description of goods or services; the amount; and your payment details. If you are a sales tax-registered business, include your STRN (Sales Tax Registration Number), the applicable tax rate, and the tax amount.',
      },
    },
    {
      '@type': 'Question',
      name: 'How do freelancers in Pakistan invoice international clients?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'Pakistani freelancers providing services to overseas clients typically invoice in USD, GBP, or EUR. Export of services is generally exempt from Pakistan sales tax. Use the international invoice type to bill in any currency with your bank details or payment platform information. Most Pakistani freelancers receive payments via Payoneer, Wise, or local bank wire transfers.',
      },
    },
    {
      '@type': 'Question',
      name: 'What is sales tax in Pakistan and when does it apply?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'Pakistan levies sales tax at 17% on taxable goods and some services under the federal Sales Tax Act. Services provided in provinces are subject to provincial sales tax at varying rates (13% in Sindh, 16% in Punjab for most services). FBR registration is required once your annual turnover exceeds PKR 10 million for businesses supplying taxable goods or services.',
      },
    },
    {
      '@type': 'Question',
      name: 'What currency should Pakistani freelancers use on invoices?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'For international clients, invoice in the client\'s currency — typically USD for US clients, GBP for UK clients, or EUR for European clients. This avoids currency conversion confusion for the client. Invilic supports 20+ currencies. For local Pakistani clients, use PKR (Pakistani Rupee).',
      },
    },
    {
      '@type': 'Question',
      name: 'Is this invoice generator free for Pakistani users?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'Yes. Invilic is completely free for Pakistani freelancers and businesses. Create professional invoices in USD, PKR, or any currency. Download a clean PDF instantly — no sign-up required for up to 5 invoices per month.',
      },
    },
  ],
};

const invoiceTypes = [
  {
    icon: Globe,
    title: 'International Invoice',
    subtitle: 'For billing overseas clients in USD/GBP/EUR',
    features: ['USD, GBP, EUR billing', 'SWIFT / bank details', 'Export service format', 'Multi-currency support', 'Professional PDF'],
    to: '/create/international',
    color: 'teal',
    btnClass: 'bg-teal-600 hover:bg-teal-700',
  },
  {
    icon: FileText,
    title: 'Standard Invoice',
    subtitle: 'For local Pakistani clients in PKR',
    features: ['PKR currency', 'Custom line items', 'Logo upload', 'NTN number field', 'Payment terms'],
    to: '/create/freelancer',
    color: 'blue',
    btnClass: 'bg-blue-600 hover:bg-blue-700',
  },
  {
    icon: Percent,
    title: 'Sales Tax Invoice',
    subtitle: 'For registered businesses',
    features: ['Tax rate field', 'STRN number field', 'Net + tax breakdown', 'PKR currency', 'FBR-ready format'],
    to: '/create/vat',
    color: 'indigo',
    btnClass: 'bg-indigo-600 hover:bg-indigo-700',
  },
];

export default function InvoiceGeneratorPakistanPage() {
  return (
    <div className="min-h-screen bg-white dark:bg-gray-950">
      <SEO
        title="Invoice Generator Pakistan: Free Invoice Maker for Pakistani Freelancers | Invilic"
        description="Free invoice generator for Pakistani freelancers and businesses. Create invoices in USD, PKR, GBP, and more. Professional PDF download. No sign-up needed."
        canonical="https://www.invilic.com/invoice-generator-pakistan"
        schema={schema}
      />

      <SeoNav />

      <main className="max-w-4xl mx-auto px-6 py-16">
        <div className="text-center mb-16">
          <span className="inline-block bg-teal-50 dark:bg-teal-900/30 text-teal-700 dark:text-teal-300 text-xs font-semibold px-3 py-1 rounded-full uppercase tracking-wider mb-4">
            🇵🇰 Pakistan
          </span>
          <h1 className="text-4xl sm:text-5xl font-extrabold text-gray-900 dark:text-white leading-tight mb-5">
            Invoice Generator Pakistan
          </h1>
          <p className="text-xl text-gray-500 dark:text-gray-400 max-w-2xl mx-auto leading-relaxed mb-8">
            Free invoice generator for Pakistani freelancers and businesses. Create professional invoices in USD, PKR, GBP, or EUR. Whether you bill international clients or local businesses, download a clean PDF in seconds — no account needed.
          </p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <Link to="/create/international" className="inline-flex items-center justify-center gap-2 bg-teal-600 hover:bg-teal-700 text-white font-semibold px-6 py-4 rounded-xl shadow-lg shadow-teal-600/20 transition-all duration-200">
              Bill International Clients <ArrowRight className="w-5 h-5" />
            </Link>
            <Link to="/create/freelancer" className="inline-flex items-center justify-center gap-2 bg-blue-600 hover:bg-blue-700 text-white font-semibold px-6 py-4 rounded-xl shadow-lg shadow-blue-600/20 transition-all duration-200">
              Create PKR Invoice <ArrowRight className="w-5 h-5" />
            </Link>
          </div>
          <p className="text-sm text-gray-400 dark:text-gray-500 mt-3">Free · No sign-up · PDF in seconds</p>
        </div>

        <section className="mb-16">
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-8 text-center">Which Invoice Do You Need?</h2>
          <div className="grid sm:grid-cols-3 gap-5">
            {invoiceTypes.map(({ icon: Icon, title, subtitle, features, to, color, btnClass }) => (
              <div key={title} className={`border border-${color}-100 dark:border-${color}-800/30 rounded-2xl p-6 bg-${color}-50 dark:bg-${color}-900/10`}>
                <div className={`w-10 h-10 rounded-xl bg-${color}-100 dark:bg-${color}-900/40 flex items-center justify-center mb-4`}>
                  <Icon className={`w-5 h-5 text-${color}-600 dark:text-${color}-400`} />
                </div>
                <h3 className="font-bold text-gray-900 dark:text-white mb-1">{title}</h3>
                <p className={`text-xs text-${color}-700 dark:text-${color}-300 mb-4`}>{subtitle}</p>
                <ul className="space-y-2 mb-5">
                  {features.map(f => (
                    <li key={f} className="flex items-center gap-2 text-xs text-gray-600 dark:text-gray-400">
                      <CheckCircle className={`w-3.5 h-3.5 text-${color}-500 flex-shrink-0`} /> {f}
                    </li>
                  ))}
                </ul>
                <Link to={to} className={`inline-flex items-center justify-center gap-1.5 w-full px-4 py-2.5 ${btnClass} text-white text-sm font-semibold rounded-xl transition-all`}>
                  Create Free <ArrowRight className="w-4 h-4" />
                </Link>
              </div>
            ))}
          </div>
        </section>

        <section className="mb-16 p-8 rounded-2xl border border-gray-100 dark:border-gray-800 bg-gray-50 dark:bg-gray-900/50">
          <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-5">International vs Local Invoice: Which Do You Need?</h2>
          <div className="grid sm:grid-cols-2 gap-6">
            <div>
              <div className="font-semibold text-teal-700 dark:text-teal-400 mb-3">Use an International Invoice if:</div>
              {[
                'Your client is based in the US, UK, Europe, or elsewhere overseas',
                'You need to bill in USD, GBP, EUR, or another foreign currency',
                'You work on Upwork, Fiverr, or other international platforms',
              ].map(p => (
                <div key={p} className="flex items-start gap-2 text-sm text-gray-700 dark:text-gray-300 mb-2">
                  <CheckCircle className="w-4 h-4 text-teal-500 flex-shrink-0 mt-0.5" /> {p}
                </div>
              ))}
            </div>
            <div>
              <div className="font-semibold text-blue-700 dark:text-blue-400 mb-3">Use a PKR Invoice if:</div>
              {[
                'Your client is based in Pakistan and pays in PKR',
                'You provide services to local Pakistani businesses',
                'You need a local business invoice with NTN details',
              ].map(p => (
                <div key={p} className="flex items-start gap-2 text-sm text-gray-700 dark:text-gray-300 mb-2">
                  <CheckCircle className="w-4 h-4 text-blue-500 flex-shrink-0 mt-0.5" /> {p}
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="mb-16">
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-8">Pakistan Invoice Generator: Frequently Asked Questions</h2>
          <div className="space-y-4">
            {schema.mainEntity.map(({ name, acceptedAnswer }) => (
              <details key={name} className="group border border-gray-100 dark:border-gray-800 rounded-xl overflow-hidden">
                <summary className="flex items-center justify-between px-6 py-4 cursor-pointer font-medium text-gray-900 dark:text-white hover:bg-gray-50 dark:hover:bg-gray-900/50 list-none">
                  {name}
                  <ArrowRight className="w-4 h-4 text-gray-400 transition-transform group-open:rotate-90 flex-shrink-0 ml-4" />
                </summary>
                <p className="px-6 pb-5 text-gray-500 dark:text-gray-400 text-sm leading-relaxed">{acceptedAnswer.text}</p>
              </details>
            ))}
          </div>
        </section>

        <section className="text-center py-12 border-t border-gray-100 dark:border-gray-800">
          <h2 className="text-3xl font-bold text-gray-900 dark:text-white mb-4">Create Your Invoice Now for Free</h2>
          <p className="text-gray-500 dark:text-gray-400 mb-8">Professional PDF in USD, PKR, or any currency. No account required.</p>
          <Link to="/create/international" className="inline-flex items-center gap-2 bg-teal-600 hover:bg-teal-700 text-white font-semibold px-8 py-4 rounded-xl shadow-lg shadow-teal-600/20 transition-all duration-200 text-lg">
            Create Invoice Free <ArrowRight className="w-5 h-5" />
          </Link>
        </section>
      </main>
    </div>
  );
}
